// "Who's booking?" — the early-identity sheet.
//
// Opens the first time an item lands in the cart and the guest is still
// anonymous. Two paths out:
//
//   * New here → first name + email or mobile, one tap. The details are
//     stamped onto the cart's holds so the venue can follow up if the guest
//     never reaches payment — the whole reason this sheet exists.
//   * Booked before → a 6-digit code by email or WhatsApp. Verifying proves
//     that one contact, and only it comes back to prefill (20260927100020):
//     the guest record it matches was typed at checkouts, so its names and
//     other contact are not returned. What the guest typed here stays.
//
// Dismissable on purpose: a guest who refuses to identify can still browse
// and book — checkout captures identity regardless, exactly as before. The
// sheet only ever shows once per cart.

import { useState } from "react";
import { useGuest, type GuestChannel } from "../state/guest";
// normalizeOptionalPhone: the details form (blank is allowed — email OR phone).
// normalizePhoneE164: the WhatsApp channel, where a number is mandatory.
import { normalizeOptionalPhone, normalizePhoneE164 } from "../lib/phone";

export function GuestIdentitySheet({ onClose }: { onClose: () => void }) {
  const { profile, saveProfile, requestCode, verifyCode } = useGuest();
  const [mode, setMode] = useState<"details" | "code">("details");
  const [channel, setChannel] = useState<GuestChannel>("email");
  const [firstName, setFirstName] = useState(profile.firstName);
  const [email, setEmail] = useState(profile.email);
  const [phone, setPhone] = useState(profile.phone);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitDetails(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() && !phone.trim()) {
      setError("Add an email or mobile number so we can hold your booking for you.");
      return;
    }
    // Store E.164, exactly as checkout does. "0655139214" was being saved raw
    // and stamped onto the holds that way — a number WhatsApp and SMS cannot
    // route, on the very rows this sheet exists to make contactable.
    const check = normalizeOptionalPhone(phone);
    if (!check.ok) {
      setError(check.error ?? "Check the mobile number — use international format, e.g. +27 82 123 4567.");
      return;
    }
    const e164 = check.e164 ?? "";
    if (e164 && e164 !== phone) setPhone(e164);
    saveProfile({ firstName: firstName.trim(), email: email.trim(), phone: e164 });
    onClose();
  }

  // Where the code goes. WhatsApp is a first-class option because WE send it,
  // through the venue's own number on the existing outbox — there is no auth
  // provider involved and nothing to configure per tenant.
  async function sendCode(via: GuestChannel) {
    setError(null);
    let destination = email.trim();
    if (via === "whatsapp") {
      const check = normalizePhoneE164(phone);
      if (!check.ok) {
        setError("Add your mobile number first, in international format — e.g. +27 82 123 4567.");
        return;
      }
      destination = check.e164!;
      if (destination !== phone) setPhone(destination);
    } else if (!destination.includes("@")) {
      setError("Type your email above first, then we'll send the code there.");
      return;
    }
    setBusy(true);
    const r = await requestCode(via, destination);
    setBusy(false);
    if (!r.ok) {
      setError(r.error ?? "Couldn't send the code — try again.");
      return;
    }
    setChannel(via);
    setCode("");
    setMode("code");
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const r = await verifyCode(
      channel,
      channel === "whatsapp" ? phone : email,
      code,
    );
    setBusy(false);
    if (!r.ok) {
      setError(r.error ?? "That code didn't match.");
      return;
    }
    onClose();
  }

  return (
    <div className="vt-identity-overlay" role="dialog" aria-modal="true" aria-label="Who's booking?">
      <div className="vt-identity-sheet">
        <button
          type="button"
          className="vt-identity-close"
          aria-label="Not now"
          onClick={onClose}
        >
          ×
        </button>

        {mode === "details" ? (
          <form onSubmit={submitDetails}>
            <h3 className="vt-identity-title">Who&rsquo;s booking?</h3>
            <p className="vt-identity-sub">
              We&rsquo;ll keep your selection under your name — and can help you
              finish later if you get interrupted.
            </p>
            <div className="vt-identity-fields">
            <div className="vt-field">
              <label>First name</label>
              <input
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="vt-field">
              <label>Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="vt-field">
              <label>Mobile number</label>
              <input
                type="tel"
                autoComplete="tel"
                placeholder="+27 82 123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => {
                  // Show the guest what will actually be stored, the moment
                  // they leave the field. The conversion always happened on
                  // Continue, but the sheet closes on the same click, so the
                  // hint below promised something nobody ever saw. A silent
                  // transformation you cannot observe is indistinguishable
                  // from one that did not run.
                  const check = normalizeOptionalPhone(phone);
                  if (check.ok && check.e164 && check.e164 !== phone) {
                    setPhone(check.e164);
                  }
                }}
              />
            </div>
            </div>
            <p className="vt-identity-hint">
              We save mobile numbers in international format — 082 123 4567 becomes +27 82 123 4567.
            </p>
            {error && <p className="vt-identity-error">{error}</p>}
            <button type="submit" className="vt-cta">
              Continue
            </button>
            <p className="vt-identity-alt-label">Booked here before? Send me a sign-in code:</p>
            <div className="vt-identity-channels">
              <button
                type="button"
                className="vt-identity-alt"
                disabled={busy}
                onClick={() => void sendCode("email")}
              >
                By email
              </button>
              <button
                type="button"
                className="vt-identity-alt"
                disabled={busy}
                onClick={() => void sendCode("whatsapp")}
              >
                On WhatsApp
              </button>
            </div>
            <p className="vt-identity-privacy">
              Used for this booking and its confirmations. Marketing is always a
              separate opt-in at checkout.
            </p>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <h3 className="vt-identity-title">Check your inbox</h3>
            <p className="vt-identity-sub">
              {channel === "whatsapp" ? "We sent a 6-digit code on WhatsApp to " : "We emailed a 6-digit code to "}
              <strong>{channel === "whatsapp" ? phone.trim() : email.trim()}</strong>. If you&rsquo;ve
              booked here before it should arrive shortly.
            </p>
            <div className="vt-field">
              <label>Code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            {error && <p className="vt-identity-error">{error}</p>}
            <button
              type="submit"
              className="vt-cta"
              disabled={busy || code.trim().length < 6}
            >
              {busy ? "Checking…" : "Sign in"}
            </button>
            <button
              type="button"
              className="vt-identity-alt"
              onClick={() => {
                setError(null);
                setMode("details");
              }}
            >
              Go back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
